import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
  makeStyles
} from "@material-ui/core";
import StageInputField from "./StageInputField";
import DatePickerACCB from "./DatePickerACCB";
import { searchAvailableRooms, bookHearingRoom } from "../service";
import ACCBTheme, { TEXT } from "../ACCBTheme";
import { BookingFeeSummary, fullDayLabel, SlotLabel } from "./RoomSlotPicker";

// Centralized variable matching your theme typography configuration
const themeFont = ACCBTheme.typography.fontFamily;

const useStyles = makeStyles((theme) => ({
  disputeNameHeading: {
    fontWeight: "600",
    fontSize: "1.2rem",
    lineHeight: "1.5rem",
    color: "black",
  },
  btnTextBlack: {
    color: TEXT.secondary,
    fontSize: "16px",
    fontWeight: "400",
    textTransform: "none",
  },
}));

const isTomorrowSelectedAndPastTime = (selectedDate) => {
  if (!selectedDate) return false;

  const now = new Date();

  // Calculate tomorrow's date
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  const isTomorrow =
    selectedDate.getDate() === tomorrow.getDate() &&
    selectedDate.getMonth() === tomorrow.getMonth() &&
    selectedDate.getFullYear() === tomorrow.getFullYear();

  if (isTomorrow) {
    const hours = now.getHours();
    const minutes = now.getMinutes();
    // 8:30pm is 20:30 (20 hours and 30 minutes)
    if (hours > 20 || (hours === 20 && minutes >= 30)) {
      return true;
    }
  }

  return false;
};

export default function BookHearingRoom({ disputeId, initialStage, initialDate, onBookingSuccess, isAdmin = false }) {
  const classes = useStyles();

  const getInitialStageValue = (stage) => {
    if (!stage) return "";
    if (typeof stage === "object") {
      return stage.id || stage.stage || stage.name || "";
    }
    return stage;
  };

  // "YYYY-MM-DD" -> local Date (midnight), so a deep-linked posting date can be
  // pre-selected in the picker without a timezone shift.
  const parseInitialDate = (ymd) => {
    if (!ymd) return null;
    const [y, m, d] = String(ymd).split("-").map(Number);
    return y && m && d ? new Date(y, m - 1, d) : null;
  };

  // State Management
  const [bookingDate, setBookingDate] = useState(() => parseInitialDate(initialDate));
  const [bookingStage, setBookingStage] = useState(() => getInitialStageValue(initialStage));

  useEffect(() => {
    setBookingStage(getInitialStageValue(initialStage));
    setBookingDate(parseInitialDate(initialDate));
    setRooms([]);
    setRoomsError("");
    setSelectedSlots([]);
    setBookingSuccess(false);
    setBookingError("");
    setRecommendedDurations([]);
  }, [disputeId, initialStage, initialDate]);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState("");
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [recommededDurations, setRecommendedDurations] = useState([]);

  const toDateStr = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${mo}-${d}`;
  };

  const getRoomKey = (room) => String(room.room_id || room.room_name);

  const getBookableSlots = (room) => {
    return (room.slots || []).filter((slot) => !slot.booked && slot.bookable);
  };

  const canBookWholeDay = (room) => {
    const slots = room.slots || [];
    return slots.length > 0 && slots.every((slot) => !slot.booked && slot.bookable);
  };

  const toSelectedSlot = (room, slot) => ({
    room_key: getRoomKey(room),
    room_id: room.room_id || room.room_name,
    room_name: room.room_name,
    slot_id: slot.slot_id,
    from_time: slot.from_time,
    to_time: slot.to_time,
  });

  const isSlotSelected = (room, slot) => {
    const roomKey = getRoomKey(room);
    return selectedSlots.some(
      (selected) => selected.room_key === roomKey && selected.slot_id === slot.slot_id
    );
  };

  const handleSlotToggle = (room, slot) => {
    if (slot.booked || !slot.bookable) return;

    const roomKey = getRoomKey(room);
    setSelectedSlots((previousSlots) => {
      const sameRoomSelected =
        previousSlots.length > 0 && previousSlots[0].room_key === roomKey;
      const currentRoomSlots = sameRoomSelected ? previousSlots : [];
      const alreadySelected = currentRoomSlots.some(
        (selected) => selected.slot_id === slot.slot_id
      );

      if (alreadySelected) {
        return currentRoomSlots.filter((selected) => selected.slot_id !== slot.slot_id);
      }

      return [...currentRoomSlots, toSelectedSlot(room, slot)];
    });
    setBookingError("");
  };

  const handleWholeDayToggle = (room, checked) => {
    const roomKey = getRoomKey(room);
    const bookableSlots = getBookableSlots(room).map((slot) => toSelectedSlot(room, slot));

    setSelectedSlots((previousSlots) => {
      if (checked) return bookableSlots;
      const sameRoomSelected =
        previousSlots.length > 0 && previousSlots[0].room_key === roomKey;
      return sameRoomSelected ? [] : previousSlots;
    });
    setBookingError("");
  };

  const handleSearchSlots = async () => {
    if (!bookingDate || !bookingStage) return;
    setRooms([]);
    setRoomsError("");
    setSelectedSlots([]);
    setBookingSuccess(false);
    setBookingError("");
    setRoomsLoading(true);

    try {
      const resp = await searchAvailableRooms(toDateStr(bookingDate), bookingStage);
      const data = resp.data;
      const room_data = data?.rooms_data;

      if (resp.status !== 200) {
        setRoomsError(room_data?.error || room_data?.detail || "Failed to fetch available slots.");
      } else {
        setRooms(Array.isArray(room_data) ? room_data : (room_data?.results || []));
        // Using the array delivered directly from your slots response object!
        setRecommendedDurations(data?.recommended_durations || []);
      }
    } catch (err) {
      setRoomsError("Failed to fetch available slots.");
    } finally {
      setRoomsLoading(false);
    }
  };

  const handleBookRoom = async () => {
    if (selectedSlots.length === 0) return;
    setBookingLoading(true);
    setBookingError("");

    const payload = {
      listing_date: toDateStr(bookingDate),
      dispute_id: disputeId,
      room_id: selectedSlots[0].room_id,
      slot_id: selectedSlots.map((slot) => slot.slot_id),
      stage: bookingStage,
    };

    try {
      const resp = await bookHearingRoom(payload);
      if (resp.status !== 200 && resp.status !== 201) {
        const errData = resp.data || {};
        setBookingError(errData.error || errData.detail || "Booking failed. Please try again.");
      } else {
        setBookingSuccess(true);
        if (onBookingSuccess) onBookingSuccess();
      }
    } catch (err) {
      setBookingError("Booking failed. Please try again.");
    } finally {
      setBookingLoading(false);
    }
  };

  if (bookingSuccess) {
    return (
      <Box style={{ padding: "1rem 0" }}>
        <Typography style={{ color: TEXT.success, fontSize: "16px", fontFamily: themeFont }}>
          Hearing room booked successfully.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Inputs Configuration Layout */}
      <Grid container spacing={2} style={{ marginBottom: "1rem" }}>
        <Grid item xs={12} sm={6}>
          <StageInputField
            value={bookingStage}
            onChange={(e) => { setBookingStage(e.target.value); setSelectedSlots([]); }}
            label="Stage"
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography style={{ color: TEXT.strong, fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.2rem', fontFamily: themeFont }}>
            Date
          </Typography>
          <DatePickerACCB value={bookingDate} onChange={setBookingDate} label="" fullWidth minDate={new Date()} />
          {!isAdmin && isTomorrowSelectedAndPastTime(bookingDate) && (
            <Typography style={{ color: TEXT.error, fontSize: "12px", marginTop: "0.25rem", fontFamily: themeFont }}>
              Cause list has been generated for tomorrow, please book the room for future dates
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Controller Buttons Row */}
      <Box display="flex" alignItems="center" style={{ gap: "1rem", flexWrap: "wrap", marginBottom: "1rem", justifyContent: "space-between" }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!bookingDate || !bookingStage || roomsLoading || (!isAdmin && isTomorrowSelectedAndPastTime(bookingDate))}
          onClick={handleSearchSlots}
          style={{ textTransform: "none" }}
        >
          {roomsLoading ? (
            <Box display="flex" alignItems="center" style={{ gap: "0.4rem" }}>
              <CircularProgress size={14} color="inherit" />
              <span>Searching…</span>
            </Box>
          ) : 'Search Available Slots'}
        </Button>

        {/* {recommededDurations?.length > 0 && (
          <Typography style={{ fontSize: "0.8rem", color: TEXT.success, fontWeight: 500, textAlign: 'center', textTransform: 'capitalize', padding: '0.2rem 0.4rem', border: '1px solid', borderRadius: '4px', fontFamily: themeFont }}>
            Recommended Duration <br /> <strong>{recommededDurations.join(" / ")}</strong>
          </Typography>
        )} */}
      </Box>

      {roomsError && (
        <Typography style={{ color: TEXT.error, fontSize: "14px", marginBottom: "1rem", fontFamily: themeFont }}>
          {roomsError}
        </Typography>
      )}

      {/* Available Rooms and Time Slots Grid */}
      {!roomsLoading && rooms.length > 0 && (
        <Box style={{ marginBottom: "1rem" }}>
          {rooms.map((room) => (
            <Box key={room.room_id || room.room_name} style={{ marginBottom: "1rem", border: "1px solid #E5E5E5", borderRadius: "6px", overflow: "hidden" }}>
              <Box style={{ background: "#F5F5F5", padding: "0.5rem 1rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.25rem 1rem" }}>
                {/* On a narrow screen the right-hand group wraps below the name
                    instead of squeezing it to a word per line. */}
                <Typography style={{ fontWeight: 600, fontSize: "0.9rem", color: TEXT.primary, flex: "1 1 8rem" }}>
                  {room.room_name || `Room ${room.room_id}`}
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
                  {room.booked && (
                    <Typography style={{ fontSize: "0.75rem", color: TEXT.error, fontWeight: 500 }}>Fully Booked</Typography>
                  )}
                  {canBookWholeDay(room) && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={getBookableSlots(room).every((slot) => isSlotSelected(room, slot))}
                          indeterminate={
                            getBookableSlots(room).some((slot) => isSlotSelected(room, slot)) &&
                            !getBookableSlots(room).every((slot) => isSlotSelected(room, slot))
                          }
                          onChange={(event) => handleWholeDayToggle(room, event.target.checked)}
                        />
                      }
                      label={fullDayLabel(room)}
                      style={{ fontSize: "0.8rem", color: TEXT.secondary, whiteSpace: "nowrap", marginRight: 0 }}
                    />
                  )}
                  {room.seating_capacity && (
                    <Typography style={{ fontSize: "0.8rem", color: TEXT.secondary, fontFamily: themeFont, whiteSpace: "nowrap" }}>
                      Seating Capacity: <strong style={{ color: TEXT.primary }}>{room.seating_capacity}</strong>
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box style={{ padding: "0.75rem 1rem", display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {(room.slots || []).map((slot) => {
                  const isSelected = isSlotSelected(room, slot);

                  // Free slots are green; booked grey; selected blue.
                  let bg = "#E8F5E9";
                  let border = "1px solid #81C784";
                  let color = "#2e7d32";
                  let cursor = "pointer";

                  if (slot.booked || !slot.bookable) {
                    bg = "#F5F5F5"; border = "1px solid #E0E0E0"; color = "#BDBDBD"; cursor = "not-allowed";
                  } else if (isSelected) {
                    bg = "#EBF3FD"; border = "1px solid #2F80ED"; color = "#2F80ED";
                  }

                  return (
                    <Box
                      key={slot.slot_id}
                      onClick={() => handleSlotToggle(room, slot)}
                      style={{
                        padding: "0.35rem 0.75rem",
                        borderRadius: "4px",
                        background: bg,
                        border,
                        color,
                        cursor,
                        fontSize: "0.8rem",
                        // Same weight either way: bold text is wider, so a selected
                        // chip grew and reflowed the row. The extra inset ring marks
                        // the selection without taking any space.
                        fontWeight: 400,
                        boxShadow: isSelected ? "inset 0 0 0 1px #2F80ED" : "none",
                        userSelect: "none",
                        position: "relative",
                        display: "flex",
                        // Time on top, its fee underneath.
                        flexDirection: "column",
                        alignItems: "center",
                        lineHeight: 1.35,
                        whiteSpace: "nowrap",
                        fontFamily: themeFont
                      }}
                      title={slot.booked || !slot.bookable ? "Already booked" : ""}
                    >
                      <SlotLabel slot={slot} />
                    </Box>
                  );
                })}
                {!(room.slots || []).length && (
                  <Typography style={{ fontSize: "0.8rem", color: TEXT.muted }}>No slots available</Typography>
                )}
              </Box>
            </Box>
          ))}

          {/* Color Indicators Legend */}
          <Box display="flex" style={{ gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
            <Box display="flex" alignItems="center" style={{ gap: "0.3rem" }}>
              <Box style={{ width: 12, height: 12, borderRadius: 2, background: "#E8F5E9", border: "1px solid #81C784" }} />
              <Typography style={{ fontSize: "0.75rem", color: TEXT.secondary }}>Available</Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: "0.3rem" }}>
              <Box style={{ width: 12, height: 12, borderRadius: 2, background: "#EBF3FD", border: "2px solid #2F80ED" }} />
              <Typography style={{ fontSize: "0.75rem", color: TEXT.secondary }}>Selected</Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: "0.3rem" }}>
              <Box style={{ width: 12, height: 12, borderRadius: 2, background: "#F5F5F5", border: "1px solid #E0E0E0" }} />
              <Typography style={{ fontSize: "0.75rem", color: TEXT.secondary }}>Booked</Typography>
            </Box>
          </Box>
        </Box>
      )}

      {bookingError && (
        <Typography style={{ color: TEXT.error, fontSize: "14px", marginBottom: "0.75rem", fontFamily: themeFont }}>
          {bookingError}
        </Typography>
      )}

      {selectedSlots.length > 0 && (
        <BookingFeeSummary
          room={rooms.find((room) => getRoomKey(room) === selectedSlots[0].room_key)}
          selectedSlotIds={selectedSlots.map((slot) => slot.slot_id)}
        />
      )}

      {selectedSlots.length > 0 && (
        <Button variant="contained" color="primary" disabled={bookingLoading} onClick={handleBookRoom} style={{ width: "100%", textTransform: "none" }}>
          {bookingLoading ? "Booking…" : `Confirm Booking (${selectedSlots.length} slot${selectedSlots.length === 1 ? "" : "s"})`}
        </Button>
      )}
    </Box>
  );
}
